import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { SelectField } from '@/components/form-fields';
import {
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import {
  commitImport,
  type DryRunResult,
  dryRun,
  guessMapping,
  IMPORT_FIELDS,
  type ImportEntity,
  listImportJobs,
  type ParsedCsv,
  parseCsv,
} from '@/lib/settings/import';
import { listLocations } from '@/lib/settings/locations';

/**
 * The spreadsheet wizard: choose what, pick the file, map the columns, see
 * what would happen, then commit.
 *
 * The dry run is the whole point. A price list arrives as somebody else's
 * export with their column names and a stray date format, and finding that
 * out row by row as it half-imports is the failure this avoids.
 */

const ENTITIES: Array<{ value: ImportEntity; label: string; hint: string }> = [
  {
    value: 'product',
    label: 'Products',
    hint: 'Creates drafts; an existing SKU updates its price.',
  },
  {
    value: 'customer',
    label: 'Customers',
    hint: 'Matched by email; existing records are updated.',
  },
  { value: 'package', label: 'Packages', hint: 'Stock arriving against a variant SKU.' },
];

const NOT_MAPPED = '__none__';

export function ImportSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('catalog.manage');
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [entity, setEntity] = useState<ImportEntity>('product');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [checked, setChecked] = useState<DryRunResult | null>(null);
  const [locationId, setLocationId] = useState('');

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
  });

  const jobs = useQuery({
    queryKey: ['import-jobs', accountId],
    queryFn: () => listImportJobs(accountId),
  });

  const reset = () => {
    setParsed(null);
    setMapping({});
    setChecked(null);
    setFileName('');

    if (fileInput.current) fileInput.current.value = '';
  };

  async function onFile(file: File) {
    const text = await file.text();
    const result = parseCsv(text);

    if (!result.headers.length) {
      toast.error('That file has no header row.');

      return;
    }

    setFileName(file.name);
    setParsed(result);
    setMapping(guessMapping(entity, result.headers));
    setChecked(null);
  }

  const check = useMutation({
    mutationFn: () => {
      if (!parsed) throw new Error('Choose a file first.');

      return dryRun(accountId, entity, parsed, mapping);
    },
    onSuccess: setChecked,
    onError: (error: Error) => toast.error(error.message),
  });

  const commit = useMutation({
    mutationFn: () => {
      if (!parsed) throw new Error('Choose a file first.');

      return commitImport(accountId, entity, parsed, mapping, fileName, locationId || undefined);
    },
    onSuccess: (result) => {
      toast.success(
        `${result.created} created, ${result.updated} updated${
          result.failed ? `, ${result.failed} failed` : ''
        }.`,
      );
      reset();
      queryClient.invalidateQueries({ queryKey: ['import-jobs', accountId] });
      queryClient.invalidateQueries({ queryKey: ['products', accountId] });
      queryClient.invalidateQueries({ queryKey: ['customers', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const fields = IMPORT_FIELDS[entity];
  const missingRequired = fields.filter((field) => field.required && !mapping[field.key]);
  const stores = (locations.data ?? []).filter((location) => location.type !== 'vehicle');

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="catalog.manage" />}

      <fieldset disabled={!canManage} className="flex flex-col gap-6">
        <SectionCard
          title="What are you importing?"
          description="One kind at a time, so the column mapping means something."
        >
          <div className="grid gap-2 sm:grid-cols-3">
            {ENTITIES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setEntity(option.value);
                  reset();
                }}
                className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                  entity === option.value ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                }`}
              >
                <span className="block font-medium">{option.label}</span>
                <span className="text-muted-foreground text-xs">{option.hint}</span>
              </button>
            ))}
          </div>

          {entity === 'package' && (
            <SelectField
              id="import-location"
              label="Arriving at"
              description="Imported stock has to be somewhere."
              value={locationId}
              options={stores.map((store) => ({ value: store.id, label: store.name }))}
              onChange={setLocationId}
            />
          )}
        </SectionCard>

        <SectionCard
          title="The file"
          description="A CSV with a header row. Nothing is uploaded: it is read in this browser and written as ordinary records."
        >
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];

                if (file) onFile(file);
              }}
            />
            <Button type="button" variant="outline" onClick={() => fileInput.current?.click()}>
              <Upload className="size-4" />
              Choose a CSV
            </Button>

            {parsed && (
              <span className="text-muted-foreground text-sm">
                {fileName} · {parsed.rows.length} rows, {parsed.headers.length} columns
              </span>
            )}

            {parsed && (
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                Clear
              </Button>
            )}
          </div>
        </SectionCard>

        {parsed && (
          <SectionCard
            title="Columns"
            description="Matched by name where the headers agree. Anything left unmapped is ignored."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map((field) => (
                <SelectField
                  key={field.key}
                  id={`map-${field.key}`}
                  label={`${field.label}${field.required ? ' *' : ''}`}
                  description={field.hint}
                  value={mapping[field.key] ?? NOT_MAPPED}
                  options={[
                    { value: NOT_MAPPED, label: 'Not imported' },
                    ...parsed.headers.map((header) => ({ value: header, label: header })),
                  ]}
                  onChange={(value) =>
                    setMapping((current) => {
                      const next = { ...current };

                      if (value === NOT_MAPPED) delete next[field.key];
                      else next[field.key] = value;

                      return next;
                    })
                  }
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => check.mutate()}
                disabled={missingRequired.length > 0 || check.isPending}
              >
                {check.isPending ? 'Checking…' : 'Check the file'}
              </Button>

              {missingRequired.length > 0 && (
                <span className="text-muted-foreground text-sm">
                  Map {missingRequired.map((field) => field.label).join(', ')} first.
                </span>
              )}
            </div>
          </SectionCard>
        )}

        {checked && (
          <SectionCard title="What would happen" description="Nothing has been written yet.">
            <dl className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border p-3">
                <dt className="text-muted-foreground text-xs">Created</dt>
                <dd className="text-lg font-semibold">{checked.create}</dd>
              </div>
              <div className="rounded-lg border p-3">
                <dt className="text-muted-foreground text-xs">Updated</dt>
                <dd className="text-lg font-semibold">{checked.update}</dd>
              </div>
              <div className="rounded-lg border p-3">
                <dt className="text-muted-foreground text-xs">Problems</dt>
                <dd className="text-lg font-semibold">{checked.issues.length}</dd>
              </div>
            </dl>

            {checked.issues.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-lg border">
                <ul className="divide-y text-sm">
                  {checked.issues.slice(0, 100).map((issue) => (
                    <li key={`${issue.row}-${issue.message}`} className="flex gap-3 px-3 py-2">
                      <span className="text-muted-foreground w-16 shrink-0">Row {issue.row}</span>
                      <span>{issue.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => commit.mutate()}
                disabled={commit.isPending || (entity === 'package' && !locationId)}
              >
                {commit.isPending ? 'Importing…' : `Import ${checked.create + checked.update} rows`}
              </Button>

              {checked.issues.length > 0 && (
                <span className="text-muted-foreground text-sm">
                  Rows with problems are still attempted; whatever the database refuses is recorded
                  as a failure.
                </span>
              )}
            </div>
          </SectionCard>
        )}
      </fieldset>

      <SectionCard
        title="Recent imports"
        description="Every row's outcome is kept, so a price that arrived wrong can be traced to the file it came from."
      >
        {jobs.data?.length ? (
          <ListShell>
            {jobs.data.map((job) => (
              <ListRow
                key={job.id}
                title={job.fileName || job.entity}
                subtitle={`${job.entity} · ${job.stats.created ?? 0} created, ${
                  job.stats.updated ?? 0
                } updated, ${job.stats.failed ?? 0} failed · ${new Date(
                  job.createdAt,
                ).toLocaleString()}`}
                badges={
                  <Badge variant={job.status === 'succeeded' ? 'default' : 'secondary'}>
                    {job.status}
                  </Badge>
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>Nothing imported yet.</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}
